// context/AuthContext.jsx
import React, { createContext, useState, useEffect, useContext } from 'react';
import { AuthAPI, UserAPI, TokenService, APIUtils } from '../services/api';

// Create the context with a default value
export const AuthContext = createContext({
  user: null,
  loading: false,
  login: () => {},
  register: () => {},
  logout: () => {},
  isAuthenticated: () => false,
  updateUser: () => {}
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load user from localStorage on initial load and validate with backend
  useEffect(() => {
    const initAuth = async () => {
      const savedUser = localStorage.getItem('user');
      const token = TokenService.getToken();
      
      if (savedUser && token) {
        try {
          const parsedUser = JSON.parse(savedUser);
          
          // Validate token by fetching fresh user data
          try {
            const freshUserData = await UserAPI.getProfile();
            // Merge saved user with fresh data
            setUser({
              ...parsedUser,
              ...freshUserData
            });
          } catch (error) {
            // console.error('Token validation failed:', error);
            // Token might be expired, clear everything
            TokenService.clear();
            localStorage.removeItem('user');
          }
        } catch (error) {
          // console.error('Error parsing saved user:', error);
          TokenService.clear();
          localStorage.removeItem('user');
        }
      }
      setIsInitialized(true);
    };

    initAuth();
  }, []);


  const login = async (username, password) => {
    setLoading(true);
    try {
      // console.log('AuthContext.login called with:', username);
      
      // Call the backend API
      const response = await AuthAPI.login(username, password);
      
      // console.log('AuthAPI.login response:', response);
      // console.log('Response type:', typeof response);
      // console.log('Response keys:', Object.keys(response));
      
      // Check if response exists
      if (!response) {
        // console.error('AuthAPI.login returned undefined or null');
        return { 
          success: false, 
          error: 'No response from server' 
        };
      }
      
      // Check if response indicates an error (ok: false or has error property)
      if (!response.ok || response.error) {
        // Login failed - extract error message
        const errorMessage = response.error || 
                            (response.status === 401 ? 'Invalid username or password' : 
                             response.status === 403 ? 'Account is locked or not active' :
                             'Login failed');
        
        // console.log('Login failed:', errorMessage);
        return { 
          success: false, 
          error: errorMessage
        };
      }
      
      // Check if login was successful (has token and user)
      if (response.token && response.user) {
        // console.log('Login successful! User:', response.user);
        // Update user state
        setUser(response.user);
        
        // Store in localStorage
        localStorage.setItem('user', JSON.stringify(response.user));
        
        return { 
          success: true, 
          user: response.user,
          token: response.token,
          message: 'Login successful!' 
        };
      } else {
        // console.log('Missing token or user in response');
        return { 
          success: false, 
          error: 'Invalid response from server' 
        };
      }
    } catch (error) {
      // console.error('Login catch error:', error);
      // console.error('Error stack:', error.stack);
      
      // Handle fetch or network errors
      let errorMessage = 'Network error. Please check your connection.';
      if (error && typeof error === 'object') {
        if (error.message) {
          errorMessage = error.message;
        } else if (error.error) {
          errorMessage = error.error;
        }
      }
      
      APIUtils.handleError(error, false);
      return { 
        success: false, 
        error: errorMessage 
      };
    } finally {
      // console.log('AuthContext.login finally block reached');
      setLoading(false);
    }
  };

  const register = async (userData) => {
    setLoading(true);
    try {
      // Call the backend API
      const response = await AuthAPI.register(userData);
      
      if (response.token && response.user) {
        // Auto-login after successful registration
        setUser(response.user);
        localStorage.setItem('user', JSON.stringify(response.user));
        
        return { 
          success: true, 
          user: response.user,
          message: response.message || 'Registration successful! Please check your email for verification.'
        };
      } else {
        return { 
          success: false, 
          error: response.error || 'Registration failed' 
        };
      }
    } catch (error) {
      // console.error('Registration error:', error);
      
      let errorMessage = 'Registration failed. Please try again.';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.data?.error) {
        errorMessage = error.data.error;
      } else if (error.data?.errors) {
        // Format validation errors
        const errorList = Object.values(error.data.errors).join(', ');
        errorMessage = errorList || 'Please check your input.';
      }
      
      APIUtils.handleError(error, false);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Call the API logout function
    AuthAPI.logout();
    
    // Clear local state
    setUser(null);
    localStorage.removeItem('user');
  };

  const isAuthenticated = () => {
    return !!user && !!TokenService.getToken();
  };

  const updateUser = async (newData) => {
    setLoading(true);
    try {
      // If updating profile via API
      if (newData.full_name || newData.country || newData.phone_number || newData.date_of_birth) {
        await UserAPI.updateProfile(newData);
      }
      
      // Update local state
      const updatedUser = { ...user, ...newData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      return { success: true, user: updatedUser };
    } catch (error) {
      // console.error('Update user error:', error);
      
      let errorMessage = 'Update failed. Please try again.';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.data?.error) {
        errorMessage = error.data.error;
      }
      
      APIUtils.handleError(error, false);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    setLoading(true);
    try {
      await UserAPI.changePassword(currentPassword, newPassword);
      return { success: true, message: 'Password changed successfully!' };
    } catch (error) {
      // console.error('Change password error:', error);
      
      let errorMessage = 'Failed to change password.';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.data?.error) {
        errorMessage = error.data.error;
      }
      
      APIUtils.handleError(error, false);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const resendVerificationEmail = async () => {
    setLoading(true);
    try {
      await UserAPI.resendVerification();
      return { 
        success: true, 
        message: 'Verification email sent successfully!' 
      };
    } catch (error) {
      // console.error('Resend verification error:', error);
      
      let errorMessage = 'Failed to send verification email.';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.data?.error) {
        errorMessage = error.data.error;
      }
      
      APIUtils.handleError(error, false);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const refreshUserProfile = async () => {
    try {
      const freshUserData = await UserAPI.getProfile();
      const updatedUser = { ...user, ...freshUserData };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return { success: true, user: updatedUser };
    } catch (error) {
      // console.error('Refresh profile error:', error);
      // Don't logout on profile refresh error, just log it
      return { success: false, error: error.message };
    }
  };

  const forgotPassword = async (email) => {
    setLoading(true);
    try {
      await AuthAPI.forgotPassword(email);
      return { 
        success: true, 
        message: 'If an account exists with this email, you will receive password reset instructions.' 
      };
    } catch (error) {
      // console.error('Forgot password error:', error);
      
      let errorMessage = 'Failed to process request.';
      if (error.message) {
        errorMessage = error.message;
      }
      
      APIUtils.handleError(error, false);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const verifyEmail = async (token) => {
    setLoading(true);
    try {
      const response = await AuthAPI.verifyEmail(token);
      
      // If email verified successfully, update user
      if (user) {
        const updatedUser = { ...user, email_verified: true };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
      
      return { 
        success: true, 
        message: response.message || 'Email verified successfully!' 
      };
    } catch (error) {
      // console.error('Verify email error:', error);
      
      let errorMessage = 'Failed to verify email.';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.data?.error) {
        errorMessage = error.data.error;
      }
      
      APIUtils.handleError(error, false);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    isInitialized,
    login,
    register,
    logout,
    isAuthenticated,
    updateUser,
    changePassword,
    resendVerificationEmail,
    refreshUserProfile,
    forgotPassword,
    verifyEmail
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

// Helper hooks for specific use cases
export const useUser = () => {
  const { user } = useAuth();
  return user;
};

export const useAuthStatus = () => {
  const { user, isInitialized } = useAuth();
  return { 
    isAuthenticated: !!user, 
    isLoading: !isInitialized,
    user 
  };
};

export const useLogin = () => {
  const { login, loading } = useAuth();
  return { login, isLoading: loading };
};

export const useLogout = () => {
  const { logout } = useAuth();
  return logout;
};

export const useRegister = () => {
  const { register, loading } = useAuth();
  return { register, isLoading: loading };
};

// Protected route component
export const ProtectedRoute = ({ children, requireAuth = true, requireVerified = false }) => {
  const { user, isInitialized } = useAuth();
  
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (requireAuth && !user) {
    // Redirect to login
    window.location.href = '/login';
    return null;
  }
  
  if (requireAuth && requireVerified && user && !user.email_verified) {
    // Redirect to verification page
    window.location.href = '/verify-email';
    return null;
  }
  
  if (!requireAuth && user) {
    // Redirect to home if already logged in
    window.location.href = '/';
    return null;
  }
  
  return children;
};

// Higher-order component for protected routes
export const withAuth = (Component, options = {}) => {
  const { requireAuth = true, requireVerified = false } = options;
  
  return function WithAuthComponent(props) {
    const { user, isInitialized } = useAuth();
    
    if (!isInitialized) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      );
    }
    
    if (requireAuth && !user) {
      window.location.href = '/login';
      return null;
    }
    
    if (requireAuth && requireVerified && user && !user.email_verified) {
      window.location.href = '/verify-email';
      return null;
    }
    
    if (!requireAuth && user) {
      window.location.href = '/';
      return null;
    }
    
    return <Component {...props} />;
  };
};